import { AnyAction, Store } from 'redux';
import { DesignerFeatures, DesignerParams, ProductTypesFilter, SprdUser } from '@/types';
import { trackStep } from '@/utils/adobeLaunch';
import { getRoute, navigate } from '@/utils/history';
import { getParams } from '@/utils/params';
import { getAppearance } from '@/model/draft/getAppearance';
import { getProductType } from '@/model/draft/getProductType';
import { loadBasketAction } from '@/redux/basket/actions/loadBasketAction';
import { getPriceBreakdown } from '@/redux/common/getters/getPriceBreakdown';
import { setFeaturesAction } from '@/redux/core/actions/setFeaturesAction';
import { setUserAction } from '@/redux/core/actions/setUserAction';
import { getCurrencyId } from '@/redux/core/getters/getCurrencyId';
import { setDraftAction } from '@/redux/draft/actions/setDraftAction';
import { getProductTypeId } from '@/redux/draft/getters/getProductTypeId';
import { getInitialDraft } from '@/redux/lazyStore';
import { fetchProductTypeAssortmentAction } from '@/redux/productTypes/actions/fetchProductTypeAssortmentAction';
import { setFilterAction } from '@/redux/productTypes/actions/setFilterAction';
import { selectAssortmentCategoryPath } from '@/redux/productTypes/getters/selectAssortmentCategoryPath';
import { selectFilter } from '@/redux/productTypes/getters/selectFilter';
import { StoreDispatch, StoreState } from '@/redux/types';

export class ExternalInterface {
  private store: Store<StoreState, AnyAction>;
  private storeDispatch: StoreDispatch;
  private productTypeId: string;
  private assortmentCategoryPath: string | null;

  constructor(store: Store<StoreState, AnyAction>) {
    this.store = store;
    this.storeDispatch = store.dispatch as StoreDispatch;
    this.productTypeId = this.getProductTypeId();
    this.store.subscribe(this.onProductTypeChange.bind(this));
    this.store.subscribe(this.onAssortmentCategoryPathChange.bind(this));
    this.assortmentCategoryPath = null;
  }

  public get interfaces(): string[] {
    return [
      'start',
      'setFeatureState',
      'getProductTypeId',
      'openPanel',
      'onLoginChanged',
      'onBasketChanged',
    ];
  }

  public get asyncInterfaces(): string[] {
    return ['start'];
  }

  public getProductTypeId() {
    return getProductTypeId(this.store.getState());
  }

  public setFeatureState(featureName: string, featureValue: boolean) {
    const features: Partial<DesignerFeatures> = {
      [featureName]: featureValue,
    };

    this.storeDispatch(setFeaturesAction(features));
  }

  public openPanel(panel: string) {
    navigate(getRoute({ panel }));
  }

  public async start(params: DesignerParams, callback?: (error?: Error) => void) {
    try {
      if (params.basketId) {
        try {
          await this.storeDispatch(loadBasketAction(params.basketId));
        } catch (e) {
          // nothing to do
        }
      }

      const [initialDraft] = await getInitialDraft(params, this.storeDispatch, this.store.getState);

      this.storeDispatch(setDraftAction(initialDraft));
      const state = this.store.getState();

      const products = [
        {
          productTypeId: getProductType(initialDraft).id,
          appearanceId: getAppearance(initialDraft).id,
          price: getPriceBreakdown(initialDraft, state).product,
        },
      ];
      trackStep('start', {
        products,
        startParameters: {
          zeroStatePanel: false,
          productTypeId: params.productTypeId,
          appearanceId: params.appearanceId,
          designId: params.designId,
          isReEntry: true,
          products,
          currencyId: getCurrencyId(state),
        },
      });

      if (params.designSearch) {
        navigate(getRoute({ designSearch: params.designSearch }));
      } else if (params.departmentId || params.productTypeCategoryId) {
        const filter: ProductTypesFilter = {};
        if (params.departmentId) {
          filter.department = params.departmentId;
        }
        if (params.productTypeCategoryId) {
          filter.category = params.productTypeCategoryId;
        }
        this.storeDispatch(setFilterAction(filter));
        this.storeDispatch(fetchProductTypeAssortmentAction);
        navigate(getRoute({ panel: 'productTypes' }));
      } else {
        navigate('');
      }

      callback && callback(undefined);
    } catch (error) {
      console.warn(error);
      callback && callback(error as Error);
    }
  }

  private onProductTypeChange() {
    const newProductTypeId = this.getProductTypeId();
    if (newProductTypeId !== this.productTypeId) {
      this.productTypeId = newProductTypeId;
      const onProductTypeChange = getParams().onProductTypeChange;
      if (onProductTypeChange) {
        onProductTypeChange(newProductTypeId);
      }
    }
  }

  private onAssortmentCategoryPathChange() {
    const newCategoryPath = selectAssortmentCategoryPath(this.store.getState());
    const filter = selectFilter(this.store.getState());
    if (newCategoryPath !== this.assortmentCategoryPath) {
      this.assortmentCategoryPath = newCategoryPath;
      const onAssortmentCategoryChange = getParams().onAssortmentCategoryChange;
      if (onAssortmentCategoryChange) {
        onAssortmentCategoryChange({ categoryPath: newCategoryPath, category: filter.category });
      }
    }
  }

  public onLoginChanged(user: SprdUser) {
    this.storeDispatch(setUserAction(user));
  }

  public onBasketChanged(basketId: string) {
    this.storeDispatch(loadBasketAction(basketId));
  }
}
